import React, { useState, useEffect } from "react";
import "../layout.css";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import NotificationBell from "../components/NotificationBell";
import {
  HomeOutlined,
  CalendarOutlined,
  UserOutlined,
  AppstoreOutlined,
  LogoutOutlined,
  MenuOutlined,
  CloseOutlined,
  SearchOutlined,
  TeamOutlined,
  FileTextOutlined,
  ScheduleOutlined,
  PlusCircleOutlined,
} from '@ant-design/icons';
import { Menu, Switch, Input, Button, Tooltip, Dropdown, Avatar } from 'antd';

function Layout({ children }) {
  const [searchInput, setSearchInput] = useState("");
  const [filteredMenu, setFilteredMenu] = useState([]);
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(window.innerWidth <= 768);
  const [mode, setMode] = useState('inline');
  const [theme, setTheme] = useState('light');
  const { user } = useSelector((state) => state.user);
  const navigate = useNavigate();

  useEffect(() => {
    if (!localStorage.getItem("token")) {
      navigate("/login");
    }
  }, [navigate]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 768) {
        setCollapsed(true);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (window.innerWidth <= 768) {
      setCollapsed(true);
    }
  }, [location.pathname]);

  const changeMode = (value) => {
    setMode(value ? 'vertical' : 'inline');
  };

  const changeTheme = (value) => {
    setTheme(value ? 'dark' : 'light');
  };

  // User Menu
  const userMenu = [
    {
      key: "1",
      icon: <HomeOutlined />,
      label: "Home",
      path: "/",
    },
    {
      key: "2",
      icon: <CalendarOutlined />,
      label: "Appointment",
      path: "/appointments",
    },
    {
      key: "3",
      icon: <FileTextOutlined />,
      label: "Appointment Feedback",
      path: "/reviewSubmission",
    },
  ];

  // Doctor Menu
  const doctorMenu = [
    {
      key: "1",
      icon: <HomeOutlined />,
      label: "Home",
      path: "/",
    },
    {
      key: "2",
      icon: <CalendarOutlined />,
      label: "My Appointments",
      path: "/doctor/appointments",
    },
    {
      key: "3",
      icon: <FileTextOutlined />,
      label: "Appointment Reviews",
      path: "/doctor/markapp-completed",
    },
    {
      key: "4",
      icon: <UserOutlined />,
      label: "Profile",
      path: `/doctor/profile/${user?._id}`,
    },
  ];

  // Registered Doctor Menu
  const regDoctorMenu = [
    {
      key: "1",
      icon: <HomeOutlined />,
      label: "Home",
      path: "/",
    },
    {
      key: "2",
      icon: <PlusCircleOutlined />,
      label: "Apply Appointment",
      path: "/apply-doctor",
    },
  ];

  // Staff Menu with submenus
  const staffMenu = [
    {
      key: "1",
      icon: <HomeOutlined />,
      label: "Home",
      path: "/staff/home",
    },
    {
      key: "sub1",
      icon: <CalendarOutlined />,
      label: "Appointments",
      children: [
        {
          key: "2",
          label: "All Appointments",
          path: "/staff/appointments-staff",
        },
        {
          key: "3",
          label: "Complete Appointment",
          path: "/staff/staff-mark-app-completed",
        },
      ],
    },
    {
      key: "sub2",
      icon: <TeamOutlined />,
      label: "Management",
      children: [
        {
          key: "4",
          label: "Patient List",
          path: "/staff/userlist",
        },
        {
          key: "5",
          label: "Doctor List",
          path: "/staff/doctorlist",
        },
      ],
    },
    {
      key: "6",
      icon: <ScheduleOutlined />,
      label: "Schedule Doctor",
      path: "/staff/doctor-schedule",
    },
    {
      key: "7",
      icon: <FileTextOutlined />,
      label: "Patient Feedback",
      path: "/staff/manage-review",
    },
  ];

  // Admin Menu with organized submenus
  const adminMenu = [
    {
      key: "1",
      icon: <HomeOutlined />,
      label: "Home",
      path: "/admin/home",
    },
    {
      key: "sub1",
      icon: <CalendarOutlined />,
      label: "Appointments",
      children: [
        {
          key: "2",
          label: "Daily Scheduled List",
          path: "/user/scheduled-patient",
        },
        {
          key: "3",
          label: "Appointment List",
          path: "/admin/appointments-admin",
        },
        {
          key: "4",
          label: "Cancel Appointment",
          path: "/admin/appointments-cancel",
        },
        {
          key: "5",
          label: "Complete Appointment",
          path: "/admin/admin-mark-app-completed",
        },
        {
          key: "6",
          label: "Appointment Report",
          path: "/admin/appointments-report",
        },
      ],
    },
    {
      key: "sub2",
      icon: <FileTextOutlined />,
      label: "Patient Management",
      children: [
        {
          key: "7",
          label: "Register Patient",
          path: "/admin/add-patient",
        },
          {
          key: "8",
          label: "Book Patient",
          path: "/admin/book-patient",
        },
      ],
    },

     {
      key: "sub3",
      icon: <FileTextOutlined />,
      label: "Feedback & Reviews",
      children: [
        {
          key: "9",
          label: "Patient Feedback",
          path: "/admin/manage-review",
        },
      ],
    },


    {
      key: "sub4",
      icon: <ScheduleOutlined />,
      label: "Scheduling",
      children: [
        {
          key: "10",
          label: "Schedule Doctor",
          path: "/admin/doctor-schedule",
        },
        {
          key: "11",
          label: "Add Doctor Slot",
          path: "/admin/slots",
        },
        {
          key: "12",
          label: "Create Clinic Day",
          path: "/admin/create-clinic-day",
        },
        {
          key: "13",
          label: "Doctor Slot Status",
          path: "/admin/doctorslots-status",
        },
      ],
    },
    {
      key: "sub5",
      icon: <TeamOutlined />,
      label: "User Management",
      children: [
        {
          key: "14",
          label: "Manage Doctors",
          path: "/admin/doctorslist",
        },
        {
          key: "15",
          label: "View Patients",
          path: "/admin/userslist",
        },
        {
          key: "16",
          label: "View Staff",
          path: "/admin/stafflist",
        },
        {
          key: "17",
          label: "View Admins",
          path: "/admin/adminlist",
        },
        {
          key: "18",
          label: "Create Staff",
          path: "/admin/staff-register",
        },
      ],
    },
    {
      key: "sub6",
      icon: <AppstoreOutlined />,
      label: "System Configuration",
      children: [
        {
          key: "19",
          label: "Add Service",
          path: "/admin/service",
        },
        {
          key: "20",
          label: "Add Location",
          path: "/admin/location",
        },
        {
          key: "21",
          label: "Add Speciality",
          path: "/admin/speciality",
        },
        {
          key: "22",
          label: "Media Upload",
          path: "/admin/media-image-uploads",
        },
      ],
    },
  ];

  const menuToBeDisplayed =
    filteredMenu.length > 0
      ? filteredMenu
      : user?.isAdmin
      ? adminMenu
      : user?.isDoctor
      ? doctorMenu
      : user?.isStaff
      ? staffMenu
      : user?.regDoctor
      ? regDoctorMenu
      : userMenu;

  const role = user?.isAdmin
    ? "Admin"
    : user?.isDoctor
    ? "Doctor"
    : user?.isStaff
    ? "Staff"
    : user?.regDoctor
    ? "Reg Doctor"
    : "User";

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("userData");
    localStorage.removeItem(`notificationDismissed_${user._id}`);
    toast.success("You have been logged out");
    toast("Redirecting to Login Page");
    navigate("/login");
    window.location.reload();
  };

  const handleSearch = (e) => {
    const keyword = e.target.value.toLowerCase();
    setSearchInput(keyword);

    if (keyword === "") {
      setFilteredMenu([]);
    } else {
      const currentMenu = user?.isAdmin
        ? adminMenu
        : user?.isDoctor
        ? doctorMenu
        : user?.isStaff
        ? staffMenu
        : user?.regDoctor
        ? regDoctorMenu
        : userMenu;

      // Flatten menu for search
      const flattenMenu = (menu) => {
        return menu.reduce((acc, item) => {
          if (item.children) {
            return [...acc, item, ...flattenMenu(item.children)];
          }
          return [...acc, item];
        }, []);
      };

      const flatMenu = flattenMenu(currentMenu);
      setFilteredMenu(
        flatMenu.filter((menu) =>
          menu.label.toLowerCase().includes(keyword)
        )
      );
    }
  };

  const handleMenuClick = ({ key }) => {
    // Find menu item in flattened structure
    const flattenMenu = (menu) => {
      return menu.reduce((acc, item) => {
        if (item.children) {
          return [...acc, ...flattenMenu(item.children)];
        }
        return [...acc, item];
      }, []);
    };

    const currentMenu = user?.isAdmin
      ? adminMenu
      : user?.isDoctor
      ? doctorMenu
      : user?.isStaff
      ? staffMenu
      : user?.regDoctor
      ? regDoctorMenu
      : userMenu;

    const flatMenu = flattenMenu(currentMenu);
    const menuItem = flatMenu.find(item => item.key === key);
    
    if (menuItem && menuItem.path) {
      navigate(menuItem.path);
    }
  };

  const getSelectedKey = () => {
    const flattenMenu = (menu) => {
      return menu.reduce((acc, item) => {
        if (item.children) {
          return [...acc, ...flattenMenu(item.children)];
        }
        return [...acc, item];
      }, []);
    };

    const currentMenu = user?.isAdmin
      ? adminMenu
      : user?.isDoctor
      ? doctorMenu
      : user?.isStaff
      ? staffMenu
      : user?.regDoctor
      ? regDoctorMenu
      : userMenu;

    const flatMenu = flattenMenu(currentMenu);
    const currentItem = flatMenu.find(item => item.path === location.pathname);
    return currentItem ? [currentItem.key] : ['1'];
  };

  const getOpenKeys = () => {
    const openKeys = [];
    const checkMenu = (menu, path) => {
      for (const item of menu) {
        if (item.children) {
          const found = item.children.some(child => child.path === path);
          if (found) {
            openKeys.push(item.key);
          }
          checkMenu(item.children, path);
        }
      }
    };

    const currentMenu = user?.isAdmin
      ? adminMenu
      : user?.isDoctor
      ? doctorMenu
      : user?.isStaff
      ? staffMenu
      : user?.regDoctor
      ? regDoctorMenu
      : userMenu;

    checkMenu(currentMenu, location.pathname);
    return openKeys;
  };

  return (
    <>
      <div className={`main ${theme}`}>
        <div className="d-flex layout">
          <div className={`sidebar ${collapsed ? "collapsed" : "active"}`}>
            <div className="sidebar-header">
              <button 
                className="toggle-btn" 
                onClick={() => setCollapsed(!collapsed)}
              >
                {collapsed ? <MenuOutlined /> : <CloseOutlined />}
              </button>
              
              <div className="logo-section">
                <h1 className="logo">AM</h1>
                {!collapsed && <h3 className="role">{role}</h3>}
              </div>

              {!collapsed && (
                <div className="theme-controls">
                  <div className="control-item">
                    <span>Vertical Mode</span>
                    <Switch 
                      size="small" 
                      onChange={changeMode} 
                      checked={mode === 'vertical'}
                    />
                  </div>
                  <div className="control-item">
                    <span>Dark Mode</span>
                    <Switch 
                      size="small" 
                      onChange={changeTheme} 
                      checked={theme === 'dark'}
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="menu-container">
              <Menu
                mode={mode}
                theme={theme}
                selectedKeys={getSelectedKey()}
                defaultOpenKeys={getOpenKeys()}
                onClick={handleMenuClick}
                inlineCollapsed={collapsed}
                items={menuToBeDisplayed}
                className="custom-menu"
              />
            </div>

            <div className="sidebar-footer">
              <Button 
                type="text" 
                icon={<LogoutOutlined />}
                onClick={handleLogout}
                className="logout-btn"
              >
                {!collapsed && "Logout"}
              </Button>
            </div>
          </div>

          <div className="content">
            <div className="header">
              <div className="header-left">
                <Tooltip title={collapsed ? "Expand Menu" : "Collapse Menu"}>
                  {collapsed ? (
                    <MenuOutlined 
                      className="menu-icon"
                      onClick={() => setCollapsed(false)}
                    />
                  ) : (
                    <CloseOutlined 
                      className="menu-icon"
                      onClick={() => setCollapsed(true)}
                    />
                  )}
                </Tooltip>
                
                <div className="header-search">
                  <Input
                    className="search-input"
                    type="text"
                    placeholder="Search menu..."
                    value={searchInput}
                    onChange={handleSearch}
                    prefix={<SearchOutlined />}
                  />
                </div>
              </div>

             <div className="header-right">
  <NotificationBell />

  <Dropdown
    menu={{
      items: [
        {
          key: "username",
          label: (
            <span style={{ fontWeight: 600, color: "#1e2d3b", cursor: "default" }}>
              {user?.name}
            </span>
          ),
          disabled: true,
        },
        {
          type: "divider",
        },
        {
          key: "role",
          label: (
            <span style={{ fontSize: 12, color: "#888", cursor: "default" }}>
              {role}
            </span>
          ),
          disabled: true,
        },
        {
          type: "divider",
        },
        {
          key: "logout",
          icon: <LogoutOutlined style={{ color: "#ff4d4f" }} />,
          label: (
            <span style={{ color: "#ff4d4f", fontWeight: 500 }}>Logout</span>
          ),
          onClick: handleLogout,
        },
      ],
    }}
    trigger={["click"]}
    placement="bottomRight"
    arrow
  >
    <div className="user-avatar-wrap">
      <Avatar
        style={{
          backgroundColor: "#217559",
          cursor: "pointer",
          fontWeight: 600,
          fontSize: 14,
        }}
        size={36}
      >
        {user?.name?.charAt(0)?.toUpperCase()}
      </Avatar>
      <span className="user-name">{user?.name}</span>
    </div>
  </Dropdown>
</div>
            </div>
            
            <div className="body">
              {children}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default Layout;